import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Play, Pause, Download, ChevronDown, PhoneOff } from 'lucide-react'
import type {
  Conference,
  ConferenceEndedBy,
  AssistantTranscript,
  ScenarioClassificationResult,
} from '@super/shared-types'
import { getTranscriptRoleDisplayName } from '@super/shared-types'
import { cn } from '@/lib/utils'

const ROLE_STYLES: Record<AssistantTranscript['role'], string> = {
  assistant:
    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 saturate-50',
  user: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 saturate-50',
  system:
    'bg-gray-100 text-gray-500 dark:bg-gray-700/40 dark:text-gray-400 saturate-50',
  staff_member:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 saturate-50',
}

const ENDED_BY_LABELS: Record<ConferenceEndedBy, string> = {
  CALLER: 'Caller hung up',
  AGENT: 'Agent ended the call',
  STAFF: 'Staff ended the call',
  SYSTEM: 'Call ended by system',
}

const endedByLabel = (endedBy: ConferenceEndedBy): string =>
  ENDED_BY_LABELS[endedBy] ?? `Ended by ${endedBy}`

// Caller hangups are the reviewable signal (dead-air abandons) — amber; the rest neutral.
const CALLER_MARKER_TONE = {
  line: 'border-amber-400 dark:border-amber-600',
  text: 'text-amber-600 dark:text-amber-400',
}
const NEUTRAL_MARKER_TONE = {
  line: 'border-gray-300 dark:border-gray-600',
  text: 'text-gray-500 dark:text-gray-400',
}
import { formatMessageTime } from '@/utils/date-formatters'
import { TimeDisplay } from '@/components/common/TimeDisplay'
import { MessageActionsMenu } from '../MessageActionsMenu'
import { voiceClient } from '@/lib/api/voice.client'
import { windowPonyfill } from '@/lib/utils/window-ponyfill'
import { useAppAuth } from '@/contexts/app-auth.context'

type HistoryEntry = NonNullable<
  ScenarioClassificationResult['evaluationHistory']
>[number]

type Reclassification = NonNullable<
  ScenarioClassificationResult['reclassifications']
>[number]

// Per-turn verb: CLASSIFY (in a scenario), HOLD (holding it), NO MATCH (pre-commit).
const VERB_STYLES: Record<string, { chip: string; target: string }> = {
  CLASSIFY: {
    chip: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    target: 'text-green-700 dark:text-green-400',
  },
  // Holding the already-committed scenario — steady state, deliberately not green.
  HOLD: {
    chip: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
    target: 'text-gray-600 dark:text-gray-300',
  },
  'NO MATCH': {
    chip: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    target: 'text-gray-500 dark:text-gray-400',
  },
}

const TurnRow: React.FC<{
  ev: HistoryEntry
  committedTurn?: number | null
  // Scenario committed at this turn (not the per-turn best match); null if uncommitted.
  activeScenario?: string | null
  recl?: Reclassification
  onHoverTurn?: (turn: number | null) => void
}> = ({ ev, committedTurn, activeScenario, recl, onHoverTurn }) => {
  const [showRanking, setShowRanking] = useState(false)
  const ranking = ev.ranking ?? []
  const isRoutedTurn = committedTurn != null && ev.turn === committedTurn

  // In a scenario → CLASSIFY (commit/switch) or HOLD; NO MATCH only pre-commit.
  const decision = (() => {
    if (recl)
      return { verb: 'CLASSIFY', target: recl.toName ?? '—', detail: null }
    if (isRoutedTurn)
      return {
        verb: 'CLASSIFY',
        target: activeScenario ?? 'match',
        detail: null,
      }
    if (activeScenario)
      // Out-of-scope turn still HOLDs; surface it as the reason.
      return {
        verb: 'HOLD',
        target: activeScenario,
        detail: ev.outOfScope
          ? `out of scope${ev.outOfScopeIntent ? ` · ${ev.outOfScopeIntent}` : ''}`
          : null,
      }
    return { verb: 'NO MATCH', target: null, detail: null }
  })()
  const verbStyle = VERB_STYLES[decision.verb]
  // Rank scenarios alongside "no match"; winner = highest scorer.
  const COMMIT_THRESHOLD = 0.8
  const rankRows = [
    ...ranking.map(r => ({
      id: r.scenarioId,
      name: r.scenarioName,
      score: r.score,
      isNoMatch: false,
    })),
    {
      id: '__no_match__',
      name: 'No match',
      score: ev.noMatchScore ?? 0,
      isNoMatch: true,
    },
  ].sort((a, b) => b.score - a.score)
  const winnerId = rankRows[0]?.id
  return (
    <div
      onMouseEnter={() => onHoverTurn?.(ev.turn)}
      onMouseLeave={() => onHoverTurn?.(null)}
      className={cn(
        'px-2 py-1.5 rounded transition-colors',
        recl || isRoutedTurn
          ? 'bg-green-50 dark:bg-green-900/20'
          : 'hover:bg-white dark:hover:bg-gray-700',
      )}
    >
      <div className="flex items-center gap-2 text-[10px]">
        <span className="flex-shrink-0 w-8 text-gray-400 font-mono">
          T{ev.turn}
        </span>
        <span
          className={cn(
            'flex-shrink-0 font-mono font-semibold px-1.5 py-0.5 rounded',
            verbStyle.chip,
          )}
        >
          {decision.verb}
        </span>
        {decision.target && (
          <>
            <span className="text-gray-300 dark:text-gray-600">&rarr;</span>
            <span
              className={cn(
                'font-semibold truncate max-w-[150px]',
                verbStyle.target,
              )}
            >
              {decision.target}
            </span>
          </>
        )}
      </div>
      {decision.detail && (
        <div className="ml-10 mt-0.5 text-[9px] font-medium text-gray-500 dark:text-gray-400">
          {decision.detail}
        </div>
      )}
      <div className="ml-10 mt-0.5 text-[10px] italic text-gray-500 dark:text-gray-400 leading-snug">
        {ev.reasoning}
      </div>
      {ranking.length > 0 && (
        <div className="ml-10 mt-1">
          <button
            type="button"
            onClick={() => setShowRanking(v => !v)}
            className="text-[9px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 font-mono"
          >
            {showRanking ? '▾' : '▸'} See ranking
          </button>
          {showRanking && (
            <div className="space-y-0.5 mt-0.5 max-w-[340px]">
              {rankRows.map(row => {
                const isWinner = row.id === winnerId
                return (
                  <div
                    key={row.id}
                    className="flex items-center gap-1.5 text-[9px] font-mono"
                  >
                    <span
                      className={cn(
                        'w-28 truncate',
                        row.isNoMatch
                          ? 'text-gray-400 italic'
                          : 'text-gray-600 dark:text-gray-300',
                        isWinner && 'font-semibold',
                      )}
                    >
                      {row.name}
                    </span>
                    <span className="relative flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                      <span
                        className={cn(
                          'block h-full',
                          row.isNoMatch
                            ? 'bg-gray-400'
                            : isWinner
                              ? 'bg-green-500'
                              : 'bg-blue-400',
                        )}
                        style={{ width: `${Math.round(row.score * 100)}%` }}
                      />
                      {/* commit threshold marker */}
                      <span
                        className="absolute top-0 bottom-0 w-px bg-gray-500/50"
                        style={{ left: `${COMMIT_THRESHOLD * 100}%` }}
                      />
                    </span>
                    <span
                      className={cn(
                        'w-8 text-right tabular-nums',
                        isWinner
                          ? 'text-gray-700 dark:text-gray-200 font-semibold'
                          : 'text-gray-500',
                      )}
                    >
                      {row.score.toFixed(2)}
                    </span>
                  </div>
                )
              })}
              <div className="text-[8px] text-gray-400 pl-[116px]">
                vertical line = {Math.round(COMMIT_THRESHOLD * 100)}% commit
                threshold
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ScenarioClassificationCard: React.FC<{
  classification: ScenarioClassificationResult
  onHoverTurn?: (turn: number | null) => void
}> = ({ classification, onHoverTurn }) => {
  const [expanded, setExpanded] = useState(false)
  const isRouted = classification.match === 'MATCH'
  const history = classification.evaluationHistory ?? []
  const totalTurns = classification.totalTurns ?? history.length
  const totalLatencyMs = history.reduce((sum, ev) => sum + ev.latencyMs, 0)
  const totalLatencySec = (totalLatencyMs / 1000).toFixed(1)

  const reclassifications = classification.reclassifications ?? []

  // First committed scenario: the first switch's fromName, else the final scenario.
  const committedName =
    reclassifications[0]?.fromName ?? classification.scenarioName ?? null
  const commitPoints: { turn: number; name: string | null }[] = []
  if (classification.routedAtTurn != null && committedName)
    commitPoints.push({
      turn: classification.routedAtTurn,
      name: committedName,
    })
  for (const rc of reclassifications)
    commitPoints.push({
      turn: rc.turn,
      name: rc.toName ?? null,
    })
  const activeScenarioAtTurn = (turn: number): string | null => {
    let active: string | null = null
    for (const point of commitPoints) {
      if (point.turn <= turn) active = point.name
      else break
    }
    return active
  }

  // At-a-glance journey: the scenario it committed to → each scenario it moved to.
  const journeySteps =
    reclassifications.length > 0
      ? [
          {
            name: reclassifications[0].fromName ?? 'scenario',
            tone: 'committed',
          },
          ...reclassifications.map(rc => ({
            name: rc.toName ?? '—',
            tone: 'reclassify',
          })),
        ]
      : []
  const JOURNEY_TONE: Record<string, string> = {
    committed:
      'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
    reclassify:
      'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  }

  const toggleExpanded = useCallback(() => setExpanded(v => !v), [])

  return (
    <div className="bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border rounded-lg shadow-sm overflow-hidden mt-2">
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
            Scenario Classification
          </span>
          {journeySteps.length > 0 ? (
            <span className="inline-flex items-center gap-1 flex-wrap">
              {journeySteps.map((step, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <span className="text-gray-300 dark:text-gray-600">
                      &rarr;
                    </span>
                  )}
                  <span
                    className={cn(
                      'text-[10px] font-semibold px-2 py-0.5 rounded-full border',
                      JOURNEY_TONE[step.tone],
                    )}
                  >
                    {step.name}
                  </span>
                </React.Fragment>
              ))}
            </span>
          ) : (
            <>
              <span className="text-[10px] text-gray-400">ended on</span>
              {isRouted && classification.scenarioName ? (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                  {classification.scenarioName}
                </span>
              ) : (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-gray-100 border-gray-300 text-gray-500">
                  no match
                </span>
              )}
            </>
          )}
        </div>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0',
            expanded && 'rotate-180',
          )}
        />
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-200 dark:border-border">
          <div className="space-y-0.5 mt-2">
            {history.map(ev => (
              <TurnRow
                key={ev.turn}
                ev={ev}
                committedTurn={classification.routedAtTurn}
                activeScenario={activeScenarioAtTurn(ev.turn)}
                recl={reclassifications.find(rc => rc.turn === ev.turn)}
                onHoverTurn={onHoverTurn}
              />
            ))}
          </div>

          {/* Footer: stats */}
          <div className="pt-2 mt-2 border-t border-gray-200 dark:border-border space-y-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-600 dark:text-gray-300 font-semibold">
                Statistics
              </span>
              {isRouted && (
                <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 font-medium">
                  Classified in {totalLatencySec}s
                </span>
              )}
              <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 font-medium">
                {totalTurns} turns
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface RecordingMessageProps {
  recordingUrl: string
  recordingDuration: number
  createdAt: Date
  conference?: Conference
  threadId?: string
  messageId?: string
  companyId?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
}

export const RecordingMessage: React.FC<RecordingMessageProps> = ({
  recordingUrl,
  recordingDuration,
  createdAt,
  conference,
  threadId,
  messageId,
  companyId,
  sessionId,
  onSessionDetailsClick,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [visualizerBars, setVisualizerBars] = useState<number[]>(
    Array(24).fill(0),
  )
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isFetchingSignedUrl, setIsFetchingSignedUrl] = useState(false)
  const [hoveredTurn, setHoveredTurn] = useState<number | null>(null)
  const { isSuperAdmin } = useAppAuth()

  const audioRef = useRef<HTMLVideoElement>(null)
  const animationRef = useRef<number | undefined>(undefined)

  const needsSignedUrl = recordingUrl?.startsWith('gs://')
  const [audioUrl, setAudioUrl] = useState<string | null>(
    !needsSignedUrl && recordingUrl ? recordingUrl : null,
  )

  // Initialize duration from message data
  useEffect(() => {
    if (recordingDuration > 0) {
      setDuration(recordingDuration)
      // Set loading to false once we have duration (audio is ready to play or can be fetched)
      setIsLoading(false)
    }
  }, [recordingDuration])

  // Fluid wave animation that travels through the bars
  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        setVisualizerBars(prev =>
          prev.map((currentHeight, index) => {
            const time = Date.now() * 0.002
            const barCount = prev.length

            // Create multiple overlapping waves traveling horizontally
            const wavePosition = (index / barCount) * Math.PI * 2

            // Primary wave - travels from left to right
            const primaryWave = Math.sin(wavePosition + time * 2) * 25 + 35

            // Secondary wave - travels from right to left at different speed
            const secondaryWave = Math.sin(-wavePosition + time * 1.3) * 20 + 25

            // Tertiary wave - slower, larger wavelength
            const tertiaryWave =
              Math.sin(wavePosition * 0.5 + time * 0.8) * 15 + 20

            // Add subtle jitter and texture
            const noise =
              Math.sin(time * 3 + index * 0.5) *
              Math.cos(time * 2.1 + index * 0.7) *
              6
            const jitter = (Math.random() - 0.5) * 3 // Small random jitter
            const microJitter = Math.sin(time * 12 + index * 2) * 2 // High-frequency micro-jitter

            // Combine all waves with jitter
            const targetHeight = Math.max(
              12,
              primaryWave +
                secondaryWave * 0.6 +
                tertiaryWave * 0.4 +
                noise +
                jitter +
                microJitter,
            )

            // Very smooth interpolation for fluid movement
            const smoothedHeight =
              currentHeight + (targetHeight - currentHeight) * 0.15
            return smoothedHeight
          }),
        )
        animationRef.current = requestAnimationFrame(animate)
      }
      animate()
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      // Gradually animate bars back to rest state
      const animateToRest = () => {
        setVisualizerBars(prev => {
          const newBars = prev.map(height => height + (20 - height) * 0.08)
          const allAtRest = newBars.every(height => Math.abs(height - 20) < 0.8)

          if (!allAtRest) {
            requestAnimationFrame(animateToRest)
          }

          return newBars
        })
      }
      animateToRest()
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPlaying])

  // Audio event listeners and smooth time updates
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => setCurrentTime(audio.currentTime)
    const updateDuration = () => {
      if (!recordingDuration) {
        setDuration(audio.duration)
      }
      setIsLoading(false)
    }
    const handleEnded = () => setIsPlaying(false)
    const handleError = (event: Event) => {
      // Only set error if we actually have an audioUrl (don't error on empty src)
      if (audioUrl) {
        console.error('Audio failed to load', event)
        setHasError(true)
        setIsLoading(false)
      }
    }
    const handleCanPlay = () => {
      setIsLoading(false)
      setHasError(false)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('canplay', handleCanPlay)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('canplay', handleCanPlay)
    }
  }, [recordingDuration, audioUrl])

  // Smooth scrubber animation with more frequent updates
  useEffect(() => {
    let timeUpdateInterval: number | undefined

    if (isPlaying && audioRef.current) {
      timeUpdateInterval = setInterval(() => {
        if (audioRef.current && !audioRef.current.paused) {
          setCurrentTime(audioRef.current.currentTime)
        }
      }, 50) as unknown as number // Update every 50ms for smooth scrubber movement
    }

    return () => {
      if (timeUpdateInterval) {
        clearInterval(timeUpdateInterval)
      }
    }
  }, [isPlaying])

  const fetchSignedUrl = async (): Promise<string> => {
    if (conference?.id) {
      const response = isSuperAdmin
        ? await voiceClient.getConferenceSignedRecordingUrlAsAdmin(
            conference.id,
          )
        : await voiceClient.getConferenceSignedRecordingUrl(conference.id)
      return response.signedUrl
    }
    const response = await voiceClient.getSignedUrl(recordingUrl)
    return response.signedUrl
  }

  const togglePlayPause = async () => {
    const audio = audioRef.current
    if (!audio || !recordingUrl) return

    if (needsSignedUrl && !audioUrl && !isFetchingSignedUrl) {
      setIsFetchingSignedUrl(true)
      setHasError(false)
      try {
        const signedUrl = await fetchSignedUrl()
        setAudioUrl(signedUrl)

        audio.src = signedUrl
        await new Promise<void>((resolve, reject) => {
          audio.addEventListener('canplay', () => resolve(), { once: true })
          audio.addEventListener(
            'error',
            () =>
              reject(
                new Error(
                  `Media failed to load: ${audio.error?.message || 'unknown error'}`,
                ),
              ),
            { once: true },
          )
          audio.load()
        })

        setIsFetchingSignedUrl(false)
        await audio.play()
        setIsPlaying(true)
      } catch (error) {
        console.error('Failed to fetch signed URL:', error)
        setIsFetchingSignedUrl(false)
        setHasError(true)
      }
      return
    }

    if (!audioUrl) return

    try {
      if (isPlaying) {
        audio.pause()
        setIsPlaying(false)
      } else {
        await audio.play()
        setIsPlaying(true)
      }
    } catch (error) {
      console.error('Audio playback error:', error)
      setHasError(true)
    }
  }

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || duration === 0) return

    const rect = event.currentTarget.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const percentage = clickX / rect.width
    const newTime = percentage * duration

    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleDownload = async () => {
    if (!recordingUrl) return

    if (needsSignedUrl && !audioUrl && !isFetchingSignedUrl) {
      setIsFetchingSignedUrl(true)
      try {
        const signedUrl = await fetchSignedUrl()
        setAudioUrl(signedUrl)
        windowPonyfill.open(signedUrl, '_blank')
        setIsFetchingSignedUrl(false)
      } catch (error) {
        console.error('Failed to fetch signed URL for download:', error)
        setIsFetchingSignedUrl(false)
      }
      return
    }

    const urlToDownload = audioUrl || recordingUrl
    windowPonyfill.open(urlToDownload, '_blank')
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const transcript = conference?.assistantTranscript || []
  const classification = conference?.scenarioClassification as
    | ScenarioClassificationResult
    | undefined

  // Map a classification user-turn number to its transcript index.
  const turnToTranscriptIndex = (turn: number) => {
    let userTurnCount = 0
    for (let i = 0; i < transcript.length; i++) {
      if (transcript[i].role === 'user') userTurnCount++
      if (userTurnCount === turn) return i
    }
    return -1
  }

  // Inline transcript markers at each transition, keyed by the transcript index after which they appear.
  const committedName =
    classification?.reclassifications?.[0]?.fromName ??
    classification?.scenarioName ??
    null
  const transcriptMarkers = new Map<number, { label: string; tone: 'green' }>()
  if (classification?.routedAtTurn != null && committedName) {
    const idx = turnToTranscriptIndex(classification.routedAtTurn)
    if (idx >= 0)
      transcriptMarkers.set(idx + 1, {
        label: `classified → ${committedName}`,
        tone: 'green',
      })
  }
  for (const rc of classification?.reclassifications ?? []) {
    const idx = turnToTranscriptIndex(rc.turn)
    if (idx < 0) continue
    transcriptMarkers.set(idx + 1, {
      label: `classified → ${rc.toName ?? '—'}`,
      tone: 'green',
    })
  }

  return (
    <>
      <div className="mb-4 flex justify-center group relative">
        <div className="max-w-[80%] min-w-[220px] flex flex-col gap-2 relative group/card">
          {threadId && messageId && (
            <div className="absolute -right-10 z-30">
              <MessageActionsMenu
                threadId={threadId}
                messageId={messageId}
                companyId={companyId}
                sessionId={sessionId}
                onSessionDetailsClick={onSessionDetailsClick}
                tooltipSide="right"
              />
            </div>
          )}
          <div className="bg-gradient-to-br from-gray-700 to-gray-800 dark:from-gray-950 dark:to-gray-900 rounded-lg p-2 max-w-[520px]">
            {transcript.length > 0 && (
              <div className="bg-gray-50 dark:bg-muted rounded-lg p-2 mb-4">
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {(() => {
                    let userTurnCount = 0
                    return transcript.map(
                      (entry: AssistantTranscript, index: number) => {
                        if (entry.role === 'user') userTurnCount++
                        const entryTurn =
                          entry.role === 'user' ? userTurnCount : null
                        const isHighlighted =
                          hoveredTurn !== null && entryTurn === hoveredTurn

                        return (
                          <React.Fragment key={index}>
                            {transcriptMarkers.has(index) &&
                              (() => {
                                const marker = transcriptMarkers.get(index)!
                                const lineCls =
                                  'border-green-400 dark:border-green-600'
                                const textCls =
                                  'text-green-600 dark:text-green-400'
                                return (
                                  <div className="flex items-center gap-2 w-full py-1.5 px-1">
                                    <div
                                      className={cn(
                                        'flex-1 border-t border-dashed',
                                        lineCls,
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        'text-[10px] font-semibold whitespace-nowrap uppercase tracking-wide',
                                        textCls,
                                      )}
                                    >
                                      {marker.label}
                                    </span>
                                    <div
                                      className={cn(
                                        'flex-1 border-t border-dashed',
                                        lineCls,
                                      )}
                                    />
                                  </div>
                                )
                              })()}
                            <div
                              className={cn(
                                'flex items-start gap-2 text-xs rounded-md transition-all duration-150',
                                isHighlighted &&
                                  'bg-amber-100 shadow-[inset_0_0_0_1px_rgba(245,158,11,0.4)]',
                              )}
                            >
                              <span
                                className={cn(
                                  'text-xxs px-1.5 py-0.5 rounded font-medium whitespace-nowrap flex-shrink-0 min-w-[4rem] text-center',
                                  ROLE_STYLES[entry.role],
                                )}
                              >
                                {getTranscriptRoleDisplayName(entry.role)}
                              </span>
                              <p className="text-gray-800 dark:text-foreground text-xs">
                                {entry.text}
                              </p>
                            </div>
                          </React.Fragment>
                        )
                      },
                    )
                  })()}
                  {conference?.endedBy &&
                    (() => {
                      const tone =
                        conference.endedBy === 'CALLER'
                          ? CALLER_MARKER_TONE
                          : NEUTRAL_MARKER_TONE
                      return (
                        <div className="flex items-center gap-2 w-full py-1.5 px-1">
                          <div
                            className={cn(
                              'flex-1 border-t border-dashed',
                              tone.line,
                            )}
                          />
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 text-[10px] font-semibold whitespace-nowrap',
                              tone.text,
                            )}
                          >
                            <PhoneOff className="w-3 h-3" />
                            {endedByLabel(conference.endedBy)}
                          </span>
                          <div
                            className={cn(
                              'flex-1 border-t border-dashed',
                              tone.line,
                            )}
                          />
                        </div>
                      )
                    })()}
                </div>
              </div>
            )}
            <video
              ref={audioRef}
              src={audioUrl || undefined}
              preload="metadata"
              style={{ display: 'none' }}
            />

            {/* Visualizer */}
            <div
              className="relative mb-3 h-6 bg-black/20 rounded cursor-pointer overflow-hidden"
              onClick={handleSeek}
            >
              <div className="absolute inset-0 flex items-end justify-center gap-px px-1">
                {visualizerBars.map((height, index) => (
                  <div
                    key={index}
                    className="bg-white/35 flex-1 min-w-[2px]"
                    style={{
                      height: `${Math.max(height, 8)}%`,
                      minHeight: '3px',
                    }}
                  />
                ))}
              </div>

              {/* Progress overlay */}
              <div
                className="absolute top-0 left-0 h-full bg-white/25 rounded transition-all duration-75 ease-out"
                style={{ width: `${progress}%` }}
              />

              {/* Playhead */}
              <div
                className="absolute top-0 h-full w-1 bg-white rounded-sm shadow-md transition-all duration-75 ease-out"
                style={{
                  left: `${progress}%`,
                  transform: 'translateX(-50%)',
                  filter: isPlaying
                    ? 'drop-shadow(0 0 4px rgba(255,255,255,0.8))'
                    : 'none',
                }}
              />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlayPause}
                  disabled={!recordingUrl || isFetchingSignedUrl}
                  className="flex-shrink-0 w-8 h-8 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition-colors"
                >
                  {isLoading || isFetchingSignedUrl ? (
                    <div className="h-3 w-3 border border-white/50 border-t-white rounded-full animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-px" />
                  )}
                </button>

                <div className="flex flex-col">
                  <span className="text-white/90 font-mono text-xs">
                    {hasError && audioUrl
                      ? 'Error loading'
                      : isLoading || isFetchingSignedUrl
                        ? 'Loading...'
                        : `${formatTime(currentTime)} / ${formatTime(duration)}`}
                  </span>
                </div>
              </div>

              <button
                onClick={handleDownload}
                disabled={!recordingUrl || isFetchingSignedUrl}
                className="flex-shrink-0 p-2 text-white/80 hover:text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                title="Download recording"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>

            {/* Fallback when there is no transcript to anchor the marker to —
                the recording keeps rolling after the hangup, so the audio
                tail is misleading without this. */}
            {conference?.endedBy && transcript.length === 0 && (
              <div className="mt-2 flex justify-center">
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  {endedByLabel(conference.endedBy)}
                </span>
              </div>
            )}
          </div>

          <TimeDisplay
            date={createdAt}
            formatter={formatMessageTime}
            className="text-xs text-gray-500 dark:text-muted-foreground text-center block"
          />
        </div>
      </div>

      {/* Standalone block so expanding it never shifts the player/transcript. */}
      {classification &&
        (classification.evaluationHistory?.length ?? 0) > 0 && (
          <div className="mb-4 px-6 max-w-[600px] mx-auto">
            <ScenarioClassificationCard
              classification={classification}
              onHoverTurn={setHoveredTurn}
            />
          </div>
        )}
    </>
  )
}
